/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { navigate } from 'gatsby';
import { getUrlParams } from '@lux/helpers';
import SEO from '../../components/SEO';
import {
  HasNoStock,
  IPlan,
  IVariant,
  ProductDetails,
  scrollToSection
} from '@common-modules/product-catalogue';
import { Spacing } from '@dls/web';
import { PreSelection } from '../../types/product.types';
import { FLOW } from '../../types/plan.types';
import CisPointOfEntry from '../../components/CisPointOfEntry';
import { accessoryActions } from '@detox/actions';
import useTranslation from '../../hooks/useTranslation';
import {
  checkDefaultVariantChanged,
  getDeliveryOptions,
  getDeviceVariants,
  getPlanGroups,
  getPlans,
  getProductSpecsFromUrl,
  getPromotions,
  setUrlAfterChangeVariant
} from '../../helpers/productDetails';
import TransactionDialog from '../../components/TransactionDialog';
import OutOfStockDialog from '../../components/OutOfStockDialog';
import TermsAndConditions from '../../components/TermsAndConditions';
import CCEPrompt from '../../components/CCEPrompt';
import ShareModal from '../../components/ShareModal/ShareModal';
import {
  hideCampaigns,
  showCampaigns
} from '../../components/LivePerson/LivePerson';
import getAemComponentByName, {
  getAemComponentByKey
} from '../../helpers/getAemComponentByName';
import { COMMON } from '../../constants';
import {
  getAccessoryMTPOSStockCache,
  getAccessoryOnlineStock,
  getAccessorySkusToCheckStock,
  getRecommendedAccessories
} from '../../helpers/accessory';
import { navigateToSpringDPlans } from '../../helpers/navigation';
import { cisGOMOPortInEntryUrl, simOnlyTncUrl } from '../../config/links';
import { phoneTagsConfig } from '../../config/phone-config';
import { AEM_CONFIG_KEYS, CONFIGS } from '../../config/common';
import { getAdditionalMetaData, getShowAccessories } from './helpers';
import { ProductDetailsWrapper } from './styles';
import { useBrowserBack } from '../../hooks/useBrowserBack/useBrowserBack';
import { IphoneState } from '../../reducers/iPhone';
import { KeyValue } from '../../types/common.types';
import { ACTION_TYPES } from '../../constants/actions';
import isFeatureFlagEnabled from '../../helpers/feature-flags';
import { CCE_IPHONE_FLOW } from '../../types/featureFlag.types';
import { getRibbon } from '../../helpers/catalogue-helpers';
import isSimOnlyPlan from '../../helpers/is-sim-only-plan';
import { setIppFilter, setRRPFlow } from '../../reducers/productCatalogs';
import { clearSpecialPromo } from '../../reducers/promotions';
import { useAuthToken } from '../../hooks/useAuthToken';
import { flattenNodes, getAemConfigs, isTagged } from '../../helpers/common';
import { getUIAMEnabledWithToken } from '../../selectors';
import { useAccessories } from '../../hooks/useAccessory';
import { accessoryRibbon } from '../../components/Accessory/AccessoryRibbon';
import { AbFeatureNames } from '../../constants/ab-feature-names';
import { useABTesting } from '@wec-core/ab-testing';
import useStockCheck, { STOCK_CHECK_TYPE } from '../../hooks/useStockCheck';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const PhoneDetails = ({
  data,
  location,
  setSelectedProduct,
  setSelectedPlan,
  setUserSelection,
  selectedPlan,
  sessionToken,
  resetByKeys,
  auth,
  cisPlanRates
}) => {
  const {
    title,
    productId,
    colours,
    smallImage,
    imageGallery,
    videoUrl,
    ribbon,
    description,
    shippingLiner,
    simSlotsperSIMCardType,
    metaTitle,
    metaDescription,
    metaKeywords,
    freeGiftsByDevice,
    freeGiftsBySku,
    promotions,
    groupId,
    whatsInTheBox,
    specifications,
    slug,
    tags,
    recommendedAccessories = [],
    groupCampaignId,
    isRoi = false,
    brand
  } = data.phone;

  const { isFeatureAllowed, isPending: abTestApiPending } = useABTesting({
    featureName: [
      AbFeatureNames.MTPOS_STOCK_CACHE,
      AbFeatureNames.CCE_ESHOP_API
    ]
  });

  const [abTestingMtposStockCache, abTestingCCEEshopEnabled] = Array.isArray(
    isFeatureAllowed
  )
    ? isFeatureAllowed
    : [false, false];

  const isMTPOSStockCacheEnabled =
    abTestingMtposStockCache && !abTestApiPending;
  const isMercuryEshopEnabled = abTestingCCEEshopEnabled && !abTestApiPending;

  // iPhone launch pre-order
  const { iphone, apigeeTokenEnabled } = useSelector<
    { iphone: IphoneState },
    KeyValue
  >(state => {
    return {
      iphone: state?.iphone,
      apigeeTokenEnabled: getUIAMEnabledWithToken(state)
    };
  });
  const { sku: iPhoneSku, validateOtpData } = iphone;
  const defaultVariant = useRef<IVariant>(null);
  const isROIPhone = groupCampaignId || isRoi;
  const isCCEEnabled = isFeatureFlagEnabled(CCE_IPHONE_FLOW);
  const [hasRoiToken, setRoiToken] = useState('');
  const [showCCEPrompt, setShowCCEPrompt] = useState(false);
  const skuVariants = useRef(data.phone.skuVariants);
  const cisPlanRatesRef = useRef(cisPlanRates);
  const additionalMetaData = getAdditionalMetaData({
    title,
    metaTitle,
    metaDescription,
    metaKeywords
  });
  const allVariableConfig = flattenNodes(data?.allVariableConfig || []);

  const [
    swimlaneConfig,
    networkSpeedMap,
    defaultPlanConfig
  ] = data?.allVariableConfig
    ? getAemConfigs(allVariableConfig, [
        AEM_CONFIG_KEYS.DETOX_ENABLE_PD_CARDS_SWIMLANE_VIEW,
        AEM_CONFIG_KEYS.DETOX_NETWORK_SPEED_MAP,
        AEM_CONFIG_KEYS.DETOX_DEFAULT_PLANS
      ])
    : [false, '', ''];
  const showPlansInSwimlane = swimlaneConfig === 'true';
  const defaultPlanIds = defaultPlanConfig?.split(',');

  useEffect(() => {
    showCampaigns(window.location.href);
    dispatch(setRRPFlow(false));
    dispatch(clearSpecialPromo());
    dispatch({
      type: ACTION_TYPES.ADDONS.PX_ADDONS_RESET
    });
    return () => hideCampaigns();
  }, []);

  useEffect(() => {
    if (iPhoneSku) {
      skuVariants.current = data.phone.skuVariants.filter(
        x => x.sku === iPhoneSku
      );
      cisPlanRatesRef.current = validateOtpData?.rates;

      if (!abTestApiPending) {
        setFormattedDeviceVariants();
      }
    }
  }, [iPhoneSku, validateOtpData, abTestApiPending]);

  const { t } = useTranslation();
  const [transactionModalVisible, toggleTransactionModalVisible] = useState(
    false
  );
  const [toggleOOSModal, setToggleOOSModal] = useState({
    show: false,
    type: null
  });
  const [groupPlansTncModal, setGroupPlansTncModal] = useState(false);
  const [tncState, setTncState] = useState('');
  const [showShareModal, setShareModal] = useState<boolean>(false);
  const shareIconsList = process.env.GATSBY_SHARE_ICONS;
  const isCIS = Boolean(cisPlanRatesRef.current);
  const { allPlanGroup, allPlan, allAccessory, allAemPages, fulfilment } = data;
  const planGroups = flattenNodes(allPlanGroup);
  const allPlans = flattenNodes(allPlan);
  const accessories = flattenNodes(allAccessory);
  const dispatch = useDispatch();
  useBrowserBack();

  const siteConfigurations = getAemComponentByName(
    allAemPages,
    COMMON.AEM_COMPONENTS.SITE_CONFIGURATIONS
  );

  const [deliveryOptions, setDeliveryOptions] = useState(
    getDeliveryOptions(
      data?.fulfilment?.preOrderLinerForProductDetails,
      data?.fulfilment?.backOrderLinerForProductDetails,
      tags,
      fulfilment?.fulfilmentList,
      shippingLiner,
      false
    )
  );
  const deviceSkus = skuVariants.current?.map(s => s.sku);
  const freebieSkus = freeGiftsByDevice?.map(s => s.sku);
  const accessorySkus = getAccessorySkusToCheckStock(
    getRecommendedAccessories(accessories, recommendedAccessories)
  );
  const { stockAvailable } = useStockCheck({
    type: STOCK_CHECK_TYPE.ALL,
    deviceSkus,
    accessorySkus,
    freebieSkus,
    groupId,
    abTestingPending: abTestApiPending,
    eshopMtposStockEnabled: abTestingMtposStockCache
  });

  const isLifeStyleAccessory = isTagged(
    tags || [],
    phoneTagsConfig['home-lifestyle'].tagName
  );
  const switchesConfigurations = getAemComponentByKey(allAemPages, 'switches');
  const showAccessories = getShowAccessories(
    isLifeStyleAccessory,
    switchesConfigurations
  );

  const setFormattedDeviceVariants = () => {
    const formattedVariants = getDeviceVariants({
      skuVariants: skuVariants.current,
      colors: colours,
      imageGallery,
      seoImageTag: smallImage.alt,
      plans: allPlans,
      groupId,
      tags,
      brand,
      stockAvailability: stockAvailable.stockDetails.devices.stock,
      stockStatus: stockAvailable.stockDetails.devices.status
    });
    setFormattedVariants(formattedVariants);
  };

  const [formattedVariants, setFormattedVariants] = useState<{
    status: boolean;
    variants: IVariant[];
  }>(null);
  const isLoading = formattedVariants?.status === undefined;
  const isChangePlan = location?.state?.isChangePlan;

  const preSelection = useRef<PreSelection>(getProductSpecsFromUrl());

  useEffect(() => {
    const users = ['user'];

    resetByKeys(users);
  }, [resetByKeys]);

  useEffect(() => {
    const planObj = { plan: selectedPlan };

    setUserSelection(planObj);
  }, [selectedPlan]);

  useAuthToken({ auth }, [apigeeTokenEnabled]);

  useEffect(() => {
    const _urlParams = getUrlParams();
    const roiToken = _urlParams.roiToken;

    if (isCCEEnabled && (roiToken || isROIPhone)) {
      setRoiToken(roiToken ? roiToken : COMMON.INVALID);
      setShowCCEPrompt(true);
      dispatch({
        type: ACTION_TYPES.IPHONE.SET_ROI_TOKEN,
        data: { roiToken, groupCampaignId }
      });
    } else if (
      apigeeTokenEnabled &&
      !abTestApiPending &&
      stockAvailable.isFetched
    ) {
      setFormattedDeviceVariants();
    }
  }, [apigeeTokenEnabled, abTestApiPending, stockAvailable]);

  const { formattedAccessories, selectedAccessories } = useAccessories({
    recommendedAccessories,
    accessories,
    stockAvailability: stockAvailable.stockDetails.accessories.stock
  });

  useEffect(() => {
    if (isChangePlan && !isLoading) {
      setTimeout(() => {
        scrollToSection('plan-section');
      }, 0);
    }
  }, [isChangePlan, isLoading]);

  const getPreBackOrderLiner = variant => {
    const selectedSkuLeadDay =
      Array.isArray(fulfilment?.leadDays) &&
      fulfilment?.leadDays
        .filter(item => !!item.sku)
        .find(item => item.sku?.indexOf(variant.sku) > -1);

    if (selectedSkuLeadDay) {
      setDeliveryOptions({
        ...deliveryOptions,
        preOrderLiner: data?.fulfilment?.preOrderLinerForProductDetails.replace(
          /{deliveryperiod}/g,
          `${selectedSkuLeadDay.deliveryPeriod}`
        ),
        backOrderLiner: data?.fulfilment?.backOrderLinerForProductDetails.replace(
          /{deliveryperiod}/g,
          `${selectedSkuLeadDay.deliveryPeriod}`
        )
      });
    }
  };

  const onChangeVariant = (variant, queryParams = location.search) => {
    if (!defaultVariant.current) {
      if (variant.deviceCode && formattedVariants?.status) {
        defaultVariant.current = variant;
      }
    } else if (checkDefaultVariantChanged(defaultVariant.current, variant)) {
      defaultVariant.current = variant;
      setUrlAfterChangeVariant(variant, slug, queryParams);
    }
    const plan = allPlans.find(plan => plan.mecId === variant.planId);
    // Set plan for retaining the plan in URL
    if (selectedPlan?.mecId !== plan?.mecId && variant.stockCount) {
      setSelectedPlan({ ...plan, selectedMonthlyTerm: variant.monthlyTerm });
      if (isSimOnlyPlan(plan)) {
        dispatch(setIppFilter(true));
      } else {
        dispatch(setIppFilter(false));
      }
    }
    getPreBackOrderLiner(variant);
  };
  const ribbonForPlan = getRibbon(ribbon, selectedPlan);
  const productData = {
    freeGiftsBySku,
    freeGiftsByDevice,
    videoUrl,
    freeGiftStockAvail: stockAvailable.stockDetails.freebies.stock,
    ribbon: ribbonForPlan?.title || ''
  };

  const handleGomoPortInClick = () => {
    if (!isCIS) navigateToSpringDPlans({ groupName: selectedPlan?.groupName });
    else {
      window.location.assign(cisGOMOPortInEntryUrl);
    }
  };

  const onNextClick = (
    variant: IVariant,
    plan: IPlan,
    accessories: IVariant[] = [],
    hasNoStock: HasNoStock
  ) => {
    if (hasNoStock) {
      setToggleOOSModal({ show: true, type: hasNoStock });
      return;
    }

    setSelectedPlan({ ...plan, selectedMonthlyTerm: variant.monthlyTerm });

    setSelectedProduct({
      ...variant,
      imageGallery,
      title,
      tags,
      shippingLiner,
      simSlotsperSIMCardType,
      recommendedAccessories
    });
    dispatch(accessoryActions.storeSelectedAccessories(accessories));
    toggleTransactionModalVisible(true);
  };

  const onOOSLinkClick = () => {
    if (window?.history.length > 2) {
      navigate(-1);
    } else {
      navigate(`/`);
    }
  };

  const toggleShareModal = () => {
    setShareModal(!showShareModal);
  };

  return (
    <>
      <SEO
        title={title}
        pathname={`/phones/${slug}`}
        description={description}
        microdata={{}}
        additionalMeta={additionalMetaData}
      />
      {!isCIS && formattedVariants?.variants?.length && (
        <Spacing top={-3}>
          <CisPointOfEntry />
        </Spacing>
      )}
      {hasRoiToken ? (
        <>
          <CCEPrompt
            cceModalToggle={showCCEPrompt}
            setCCEModalToggle={(isNextBtnClick = false, navigatePath = '/') => {
              setShowCCEPrompt(false);
              setRoiToken(null);
              if (!isNextBtnClick) {
                navigate(navigatePath, { replace: true });
              }
            }}
            data={data.phone}
            roiToken={iphone.roiToken}
            onChangeVariant={onChangeVariant}
            allPlans={allPlans}
            isROIPhone={isROIPhone}
            mercuryEshopEnabled={isMercuryEshopEnabled}
          />
        </>
      ) : (
        <ProductDetailsWrapper>
          <ProductDetails
            data={{
              productDetails: {
                ...productData,
                deviceId: productId,
                variants: formattedVariants?.variants || [],
                promotions: getPromotions({ selectedPlan, promotions, isCIS }),
                accessories: formattedAccessories,
                plan: {
                  title: t('CHOOSE_YOUR_SERVICE'),
                  planGroups: getPlanGroups(
                    planGroups,
                    allPlans,
                    isCIS ? FLOW.CIS : FLOW.RES
                  ),
                  allPlans: getPlans(
                    allPlans,
                    cisPlanRatesRef.current,
                    networkSpeedMap,
                    defaultPlanIds
                  )
                },
                preSelectionOptions: {
                  storage: preSelection.current?.storage,
                  color: preSelection.current?.color,
                  plan: preSelection.current?.plan || selectedPlan?.planName,
                  monthlyTerm: preSelection.current?.monthlyTerm,
                  accessories: (selectedAccessories ?? []).map(
                    accessoryVariant => accessoryVariant.id
                  )
                },
                deliveryOptions
              },
              productModel: {
                brandCode: '',
                brandDescription: 'selectedPhone.brand',
                modelCode: productId,
                modelDescription: title,
                whatsInTheBox: whatsInTheBox,
                specifications: specifications
              }
            }}
            configs={{
              maxNumberOfAccessories: CONFIGS.ACCESSORIES_LIMIT,
              showOOSNotification: !isLoading,
              showAccessories,
              isLoading: isLoading,
              moduleLoaders: {
                accessory: formattedAccessories === undefined
              },
              dataMapping: {
                deviceId: 'modelCode',
                brandId: 'brandCode',
                deviceTitle: 'modelDescription',
                imageFile: 'image',
                productCost: 'phoneCost'
              },
              baseUrl: location.origin,
              isQuantityRequired: false,
              simOnlyTncUrl: simOnlyTncUrl,
              oosUnderColor: true,
              extendSpecsByDefault: true,
              showShareBtn:
                !isLoading &&
                shareIconsList &&
                shareIconsList !== COMMON.SHARE_ICON.NONE,
              showPlansInSwimlane,
              usePricePlanCard: true
            }}
            localisation={{
              EQUIPMENT_MOBILE_DEVICE_TITLE: 'Phones'
            }}
            callbacks={{
              onChangeVariant,
              onNextClick,
              onOOSLinkClick,
              checkStock: isMTPOSStockCacheEnabled
                ? getAccessoryMTPOSStockCache
                : getAccessoryOnlineStock,
              renderAccessoryRibbon: accessoryRibbon(isCIS),
              onShareClick: toggleShareModal
            }}
          />
          <TransactionDialog
            open={transactionModalVisible}
            isGomoToSingtelEnabled={
              siteConfigurations?.switches?.DETOX_GOMO_TO_SINGTEL
            }
            onClickGomoPortIn={handleGomoPortInClick}
            onClose={() => toggleTransactionModalVisible(false)}
            data-testid="transaction-selection"
          />
          <OutOfStockDialog
            type={toggleOOSModal.type}
            isModalOpen={toggleOOSModal.show}
            onProceedClick={onOOSLinkClick}
            onClose={() =>
              setToggleOOSModal(prevState => ({ ...prevState, show: false }))
            }
            data-testid="out-of-stock-dialog"
          />
          <TermsAndConditions
            isModalOpen={groupPlansTncModal}
            setIsModalOpen={setGroupPlansTncModal}
            tncType={tncState}
            setTncType={setTncState}
          />
          {shareIconsList && shareIconsList !== COMMON.SHARE_ICON.NONE && (
            <ShareModal showModal={showShareModal} onClose={toggleShareModal} />
          )}
        </ProductDetailsWrapper>
      )}
    </>
  );
};
